import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import api from '@/lib/api';
import { TermsAndConditions } from '@/types';
import { PlusIcon, TrashIcon, CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

export default function TermsAndConditionsEditor() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  
  const { data: terms = [], isLoading } = useQuery({
    queryKey: ['termsAndConditions'],
    queryFn: async () => {
      const { data } = await api.get('/settings/terms');
      return data.data || [];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post('/settings/terms', payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['termsAndConditions'] });
      toast.success(t('termsManagement.created'));
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: any }) => {
      const { data } = await api.put(`/settings/terms/${id}`, payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['termsAndConditions'] });
      toast.success(t('termsManagement.updated'));
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/settings/terms/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['termsAndConditions'] });
      toast.success(t('termsManagement.deleted'));
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [addingSection, setAddingSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [newSectionContent, setNewSectionContent] = useState('');
  const [formData, setFormData] = useState({
    title: t('termsManagement.title'),
    language: 'en',
    content: {} as Record<string, any>,
    isDefault: false,
  });

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
    { code: 'nl', label: 'Nederlands' },
    { code: 'es', label: 'Español' },
  ];

  const handleAddNew = () => {
    setEditingId(null);
    setExpandedSections([]);
    setAddingSection(false);
    setNewSectionName('');
    setNewSectionContent('');
    setFormData({
      title: t('termsManagement.title'),
      language: 'en',
      content: {},
      isDefault: false,
    });
    setShowForm(true);
  };

  const handleAddSection = () => {
    if (newSectionName.trim()) {
      setFormData({
        ...formData,
        content: {
          ...formData.content,
          [newSectionName]: newSectionContent,
        },
      });
      setNewSectionName('');
      setNewSectionContent('');
      setAddingSection(false);
    }
  };

  const handleEdit = (tc: TermsAndConditions) => {
    setEditingId(tc.id);
    setExpandedSections([]);
    setAddingSection(false);
    setNewSectionName('');
    setNewSectionContent('');
    setFormData({
      title: tc.title,
      language: tc.language,
      content: tc.content,
      isDefault: tc.isDefault,
    });
    setShowForm(true);
  };

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) =>
      prev.includes(sectionKey)
        ? prev.filter((k) => k !== sectionKey)
        : [...prev, sectionKey]
    );
  };

  const updateContentField = (sectionKey: string, fieldKey: string, value: string) => {
    setFormData({
      ...formData,
      content: {
        ...formData.content,
        [sectionKey]: {
          ...(formData.content[sectionKey] as Record<string, any>),
          [fieldKey]: value,
        },
      },
    });
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, payload: formData });
      } else {
        await createMutation.mutateAsync(formData);
      }
      setShowForm(false);
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm(t('common.confirmDelete'))) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleSetDefault = async (tc: TermsAndConditions) => {
    await updateMutation.mutateAsync({
      id: tc.id,
      payload: {
        ...tc,
        isDefault: true,
      },
    });
  };

  if (isLoading) {
    return <div className="p-6">{t('common.loading')}</div>;
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">{t('termsManagement.title')}</h1>
          <p className="text-gray-600">{t('termsManagement.description')}</p>
        </div>
        <button
          onClick={handleAddNew}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <PlusIcon className="w-5 h-5" />
          {t('termsManagement.addNew')}
        </button>
      </div>

      {showForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">
            {editingId ? t('common.edit') : t('common.create')}
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('termsManagement.title')}
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('termsManagement.language')}
              </label>
              <select
                value={formData.language}
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                disabled={!!editingId}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Content Sections</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
                {Object.entries(formData.content).map(([sectionKey, sectionData]: [string, any]) => (
                  <div key={sectionKey} className="bg-white rounded border border-gray-200">
                    <button
                      onClick={() => toggleSection(sectionKey)}
                      className="w-full flex items-center justify-between p-3 hover:bg-gray-50"
                    >
                      <span className="font-medium text-sm">{sectionKey}</span>
                      <ChevronDownIcon
                        className={`w-4 h-4 transition-transform ${
                          expandedSections.includes(sectionKey) ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    {expandedSections.includes(sectionKey) && (
                      <div className="border-t border-gray-200 p-3 space-y-3 bg-gray-50">
                        {typeof sectionData === 'object' && sectionData !== null ? (
                          Object.entries(sectionData).map(([fieldKey, fieldValue]: [string, any]) => (
                            <div key={fieldKey}>
                              <label className="block text-xs font-medium text-gray-600 mb-1 capitalize">
                                {fieldKey}
                              </label>
                              <textarea
                                value={typeof fieldValue === 'string' ? fieldValue : JSON.stringify(fieldValue)}
                                onChange={(e) => updateContentField(sectionKey, fieldKey, e.target.value)}
                                rows={3}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          ))
                        ) : (
                          <textarea
                            value={typeof sectionData === 'string' ? sectionData : JSON.stringify(sectionData)}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                content: { ...formData.content, [sectionKey]: e.target.value },
                              })
                            }
                            rows={3}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {addingSection && (
                  <div className="bg-white rounded border border-blue-200 p-3 space-y-2">
                    <input
                      type="text"
                      placeholder="Section name (e.g., Scope, Payment Terms)"
                      value={newSectionName}
                      onChange={(e) => setNewSectionName(e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <textarea
                      placeholder="Section content"
                      value={newSectionContent}
                      onChange={(e) => setNewSectionContent(e.target.value)}
                      rows={3}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleAddSection}
                        disabled={!newSectionName.trim()}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        Add
                      </button>
                      <button
                        onClick={() => {
                          setAddingSection(false);
                          setNewSectionName('');
                          setNewSectionContent('');
                        }}
                        className="px-3 py-1 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {Object.keys(formData.content).length === 0 && !addingSection && (
                  <div className="text-center py-6">
                    <p className="text-sm text-gray-500 mb-3">No sections yet. Add one to get started.</p>
                    <button
                      onClick={() => setAddingSection(true)}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                    >
                      Add Section
                    </button>
                  </div>
                )}

                {Object.keys(formData.content).length > 0 && !addingSection && (
                  <button
                    onClick={() => setAddingSection(true)}
                    className="w-full px-3 py-2 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded text-sm font-medium"
                  >
                    + Add Another Section
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isDefault"
                checked={formData.isDefault}
                onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="isDefault" className="text-sm text-gray-700">
                {t('termsManagement.isDefault')}
              </label>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isSaving ? t('common.saving') : t('common.save')}
              </button>
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {terms.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {t('termsManagement.noTermsFound')}
        </div>
      ) : (
        <div className="space-y-4">
          {terms.map((tc) => (
            <div
              key={tc.id}
              className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:shadow-md"
            >
              <div className="flex-1">
                <h3 className="font-semibold">{tc.title}</h3>
                <p className="text-sm text-gray-600">
                  {languages.find((l) => l.code === tc.language)?.label}
                </p>
                {tc.isDefault && (
                  <span className="inline-block mt-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                    {t('termsManagement.setAsDefault')}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                {!tc.isDefault && (
                  <button
                    onClick={() => handleSetDefault(tc)}
                    className="p-2 text-gray-600 hover:text-blue-600"
                    title={t('termsManagement.setAsDefault')}
                  >
                    <CheckIcon className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={() => handleEdit(tc)}
                  className="p-2 text-gray-600 hover:text-blue-600"
                  title={t('common.edit')}
                >
                  ✏️
                </button>
                <button
                  onClick={() => handleDelete(tc.id)}
                  className="p-2 text-gray-600 hover:text-red-600"
                  title={t('common.delete')}
                  disabled={deleteMutation.isPending}
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
